import React, {memo, useCallback, useState} from 'react'
import {View} from 'react-native'
import {RichText as RichTextApi} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {type WordGroupBio} from '#/lib/api/word-groups'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {logger} from '#/logger'
import {useAgent, useSession} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Link} from '#/components/Link'
import {Loader} from '#/components/Loader'
import {RichText} from '#/components/RichText'
import {SubtleHover} from '#/components/SubtleHover'
import {Text} from '#/components/Typography'

/**
 * Lightweight profile card that uses WordGroupBio data directly
 * without requiring a full Bluesky profile fetch upfront.
 * Fetches full profile on-demand when follow button is clicked.
 */
let WordGroupProfileCard = ({
  bio,
  position,
}: {
  bio: WordGroupBio
  position: number
}): React.ReactNode => {
  const t = useTheme()
  const {_} = useLingui()
  const {currentAccount, hasSession} = useSession()
  const isMe = bio.did === currentAccount?.did

  // Parse bio description for rich text
  const rt = React.useMemo(() => {
    const richText = new RichTextApi({text: bio.description || ''})
    richText.detectFacetsWithoutResolution()
    return richText
  }, [bio.description])

  return (
    <Link
      label={_(msg`View ${bio.displayName || sanitizeHandle(bio.handle)}'s profile`)}
      to={{
        screen: 'Profile',
        params: {name: bio.did},
      }}
      style={[a.flex_1]}
      onPress={() => {
        logger.metric(
          'suggestedUser:press',
          {
            logContext: 'Explore',
            position,
          },
          {statsig: true},
        )
      }}>
      {({hovered, pressed}) => (
        <>
          <SubtleHover hover={hovered || pressed} />
          <View
            style={[
              a.flex_1,
              a.w_full,
              a.py_lg,
              a.px_lg,
              a.border_t,
              t.atoms.border_contrast_low,
            ]}>
            <View style={[a.w_full, a.flex_1, a.gap_xs]}>
              {/* Header row with avatar, name/handle, and follow button */}
              <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                <UserAvatar
                  size={40}
                  avatar={bio.avatar}
                  type="user"
                />
                <View style={[a.flex_1]}>
                  <Text
                    emoji
                    style={[a.text_md, a.font_bold, a.leading_snug]}
                    numberOfLines={1}>
                    {sanitizeDisplayName(bio.displayName || sanitizeHandle(bio.handle))}
                  </Text>
                  <Text
                    emoji
                    style={[a.leading_snug, t.atoms.text_contrast_medium]}
                    numberOfLines={1}>
                    {sanitizeHandle(bio.handle, '@')}
                  </Text>
                </View>
                {hasSession && !isMe && (
                  <OnDemandFollowButton
                    did={bio.did}
                    displayName={bio.displayName || bio.handle}
                    position={position}
                  />
                )}
              </View>
              {/* Description */}
              {bio.description ? (
                <View style={[a.pt_xs]}>
                  <RichText
                    value={rt}
                    style={[a.leading_snug]}
                    numberOfLines={2}
                    disableLinks
                  />
                </View>
              ) : null}
            </View>
          </View>
        </>
      )}
    </Link>
  )
}
WordGroupProfileCard = memo(WordGroupProfileCard)
export {WordGroupProfileCard}

/**
 * Follow button that fetches the full profile on-demand
 * to get viewer state before performing follow action.
 */
let OnDemandFollowButton = ({
  did,
  displayName,
  position,
}: {
  did: string
  displayName: string
  position: number
}): React.ReactNode => {
  const {_} = useLingui()
  const agent = useAgent()
  const [isFollowing, setIsFollowing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const onPressFollow = useCallback(
    async (e: any) => {
      e.preventDefault()
      e.stopPropagation()

      if (isFollowing || isLoading) return

      setIsLoading(true)
      try {
        // Fetch profile to check if already following
        const profileRes = await agent.getProfile({actor: did})
        const profile = profileRes.data

        if (profile.viewer?.following) {
          // Already following
          setIsFollowing(true)
          setIsLoading(false)
          return
        }

        // Perform follow
        await agent.follow(did)
        setIsFollowing(true)
        Toast.show(_(msg`Following ${displayName}`))

        logger.metric(
          'suggestedUser:follow',
          {
            logContext: 'Explore',
            location: 'Card',
            position,
          },
          {statsig: true},
        )
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          Toast.show(_(msg`An issue occurred, please try again.`), 'xmark')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [_, agent, did, isFollowing, isLoading, displayName, position],
  )

  if (isFollowing) {
    return (
      <Button
        label={_(msg`Following`)}
        size="small"
        variant="solid"
        color="secondary"
        disabled>
        <ButtonText>{_(msg`Following`)}</ButtonText>
      </Button>
    )
  }

  return (
    <Button
      label={_(msg`Follow`)}
      size="small"
      variant="solid"
      color="primary"
      onPress={onPressFollow}
      disabled={isLoading}>
      {isLoading ? (
        <Loader size="xs" />
      ) : (
        <ButtonText>{_(msg`Follow`)}</ButtonText>
      )}
    </Button>
  )
}
OnDemandFollowButton = memo(OnDemandFollowButton)
